/**
 * Authentication Redux slice
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserProfile, AsyncState } from '@/types';

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  loading: AsyncState;
  error: string | null;
  verificationId: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  loading: 'idle',
  error: null,
  verificationId: null,
};

// Async thunks would be added here for Firebase auth operations
// For now, we'll add placeholder actions

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set user after successful authentication
     */
    setUser: (state, action: PayloadAction<UserProfile>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isAdmin = action.payload.isAdmin;
      state.loading = 'succeeded';
      state.error = null;
    },

    /**
     * Clear user on logout
     */
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.loading = 'idle';
      state.error = null;
      state.verificationId = null;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<AsyncState>) => {
      state.loading = action.payload;
    },

    /**
     * Set error
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = 'failed';
    },

    /**
     * Set verification ID for OTP
     */
    setVerificationId: (state, action: PayloadAction<string>) => {
      state.verificationId = action.payload;
    },

    /**
     * Clear verification ID
     */
    clearVerificationId: (state) => {
      state.verificationId = null;
    },

    /**
     * Update user profile
     */
    updateUserProfile: (state, action: PayloadAction<Partial<UserProfile>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

// Export actions
export const {
  setUser,
  clearUser,
  setLoading,
  setError,
  setVerificationId,
  clearVerificationId,
  updateUserProfile,
} = authSlice.actions;

// Selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsAdmin = (state: { auth: AuthState }) => state.auth.isAdmin;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectVerificationId = (state: { auth: AuthState }) => state.auth.verificationId;

export default authSlice.reducer;
